import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Plus,
  MessageSquare,
  FolderOpen,
  FolderClosed,
  LayoutDashboard,
  BarChart3,
  Bot,
  Database,
  FileText,
  Cog,
  ArrowLeft,
  Loader2,
  Building2
} from 'lucide-react';
import UserMenu from './UserMenu';
import { getUserSessions } from '../../../services/chatService';
import { Session } from '../../../types/chat';
import { CHAT_EVENTS } from '../../../utils/chatEvents';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * HamburgerIcon - Animated hamburger menu icon
 * Transforms from hamburger (3 lines) to X when expanded
 * Has scale effect on hover
 */
const HamburgerIcon = ({ isExpanded }: { isExpanded: boolean }) => (
  <div className="w-5 h-5 relative flex flex-col justify-center items-center flex-shrink-0">
    <span
      className={`
        block w-[16px] h-[2px] bg-current rounded-sm absolute
        transition-all duration-300 ease-[cubic-bezier(0.68,-0.55,0.265,1.55)]
        ${isExpanded ? 'translate-y-0 rotate-45' : '-translate-y-[5px]'}
      `}
    />
    <span
      className={`
        block w-[16px] h-[2px] bg-current rounded-sm absolute
        transition-all duration-300 ease-[cubic-bezier(0.68,-0.55,0.265,1.55)]
        ${isExpanded ? 'opacity-0 -translate-x-[10px]' : 'opacity-100 translate-x-0'}
      `}
    />
    <span
      className={`
        block w-[16px] h-[2px] bg-current rounded-sm absolute
        transition-all duration-300 ease-[cubic-bezier(0.68,-0.55,0.265,1.55)]
        ${isExpanded ? 'translate-y-0 -rotate-45' : 'translate-y-[5px]'}
      `}
    />
  </div>
);

/**
 * AnimatedIcon - Wrapper for icons with hover animations
 */
const AnimatedIcon = ({
  id,
  isActive,
  children
}: {
  id: string;
  isActive: boolean;
  children: React.ReactNode;
}) => {
  // Different animations based on icon type
  const getAnimationClass = () => {
    switch (id) {
      case 'new-chat':
        // Plus icon rotates
        return 'group-hover:rotate-90';
      case 'conversations':
        // Message icon bounces/shakes
        return 'group-hover:animate-[wiggle_0.3s_ease-in-out]';
      case 'projects':
        // Folder opens - handled separately
        return '';
      case 'dashboard':
        // Dashboard pulses
        return 'group-hover:scale-110';
      case 'agents':
        // Bot waves
        return 'group-hover:animate-[wiggle_0.3s_ease-in-out]';
      case 'databases':
        // Database pulses
        return 'group-hover:scale-110';
      case 'datasources':
        // File bounces
        return 'group-hover:-translate-y-0.5';
      case 'settings':
        // Cog rotates smoothly
        return 'group-hover:rotate-90';
      case 'workspaces':
        // Building icon scales
        return 'group-hover:scale-110';
      default:
        return 'group-hover:scale-110';
    }
  };

  return (
    <div className={`
      w-5 h-5 flex-shrink-0
      transition-all duration-300 ease-out
      ${isActive ? 'text-[#1a1a1a]' : ''}
      ${getAnimationClass()}
    `}>
      {children}
    </div>
  );
};

/**
 * FolderIcon - Animated folder that opens on hover
 */
const FolderIcon = ({ isActive, isHovered }: { isActive: boolean; isHovered: boolean }) => {
  const iconClass = `w-5 h-5 transition-all duration-200 ${isActive ? 'text-[#1a1a1a]' : ''}`;

  return isHovered && !isActive ? (
    <FolderOpen className={iconClass} />
  ) : (
    <FolderClosed className={iconClass} />
  );
};

interface SidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
  isMobile?: boolean;
}

/**
 * Sidebar - Navegação principal da aplicação
 *
 * Estados:
 * - Colapsado: apenas ícones (60px)
 * - Expandido: ícones + labels (250px)
 *
 * Features:
 * - Animated icons on hover
 * - Smooth expand/collapse transition
 * - User menu modal
 */
const Sidebar = ({ isExpanded, onToggle, isMobile = false }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  // In mobile mode, always show content when sidebar is open (drawer mode)
  // In desktop mode, show content only when expanded
  const showContent = isMobile || isExpanded;

  // Recent chats state with cache
  const [recentChats, setRecentChats] = useState<Session[]>([]);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  // Cache duration: 30 seconds (avoid refetching on quick open/close)
  const CACHE_DURATION = 30 * 1000;

  // Check if current route is admin
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Refs to avoid useCallback dependency loop
  const recentChatsRef = useRef(recentChats);
  const lastFetchTimeRef = useRef(lastFetchTime);

  // Keep refs in sync with state
  useEffect(() => {
    recentChatsRef.current = recentChats;
    lastFetchTimeRef.current = lastFetchTime;
  }, [recentChats, lastFetchTime]);

  // Generate initials from name (first letter of first name + first letter of last name)
  // "Erick" -> "E", "Erick de Souza" -> "ES"
  const getInitials = (name: string): string => {
    if (!name || name.length === 0) return 'U';
    const words = name.trim().split(/\s+/).filter(word => word.length > 0);
    if (words.length === 0) return 'U';
    if (words.length === 1) {
      // Single name: just first letter
      return words[0].charAt(0).toUpperCase();
    }
    // Multiple words: first letter of first word + first letter of last word
    const firstInitial = words[0].charAt(0).toUpperCase();
    const lastInitial = words[words.length - 1].charAt(0).toUpperCase();
    return firstInitial + lastInitial;
  };

  // Derived user data from AuthContext
  const userName = user?.name || '';
  const userEmail = user?.email || '';
  const userInitials = userName ? getInitials(userName) : 'U';

  // GRANULAR ACCESS: User has admin access if they have ANY administrative permission
  // This allows users with specific permissions (e.g., can_manage_agents) to access admin area
  // and see only the menu items they have permission for
  const hasAnyAdminPermission =
    user?.isWorkspaceAdmin ||     // Full workspace admin
    user?.canManageAgents ||       // Can manage agents/knowledge bases/data sources
    user?.canViewAnalytics ||      // Can view analytics dashboard
    user?.isAdmin ||               // Legacy role-based admin (fallback)
    false;

  // Keep backward compatibility with legacy isAdmin check
  const isAdmin = hasAnyAdminPermission;

  // Function to load recent chats with cache support
  const loadRecentChats = useCallback(async (forceRefresh = false) => {
    if (isAdminRoute) return;

    // Check cache - skip fetch if data is fresh (unless forced)
    const now = Date.now();
    if (!forceRefresh && recentChatsRef.current.length > 0 && (now - lastFetchTimeRef.current) < CACHE_DURATION) {
      return; // Use cached data
    }

    setIsLoadingChats(true);
    try {
      const response = await getUserSessions(10, 0);
      if (response.success) {
        setRecentChats(response.data);
        setLastFetchTime(now);
      }
    } catch (err) {
      console.error('Failed to load recent chats:', err);
    } finally {
      setIsLoadingChats(false);
    }
  }, [isAdminRoute, CACHE_DURATION]);

  // Load recent chats when sidebar expands
  useEffect(() => {
    if (isExpanded) {
      loadRecentChats();
    }
  }, [isExpanded, loadRecentChats]);

  // Listen for chat events to refresh list reactively (force refresh to bypass cache)
  useEffect(() => {
    const handleChatEvent = () => {
      if (isExpanded) {
        loadRecentChats(true); // Force refresh on chat events
      } else {
        // Invalidate cache so next open will fetch fresh data
        setLastFetchTime(0);
      }
    };

    // Subscribe to all chat events
    window.addEventListener(CHAT_EVENTS.SESSION_CREATED, handleChatEvent);
    window.addEventListener(CHAT_EVENTS.SESSION_UPDATED, handleChatEvent);
    window.addEventListener(CHAT_EVENTS.SESSIONS_REFRESH, handleChatEvent);

    return () => {
      window.removeEventListener(CHAT_EVENTS.SESSION_CREATED, handleChatEvent);
      window.removeEventListener(CHAT_EVENTS.SESSION_UPDATED, handleChatEvent);
      window.removeEventListener(CHAT_EVENTS.SESSIONS_REFRESH, handleChatEvent);
    };
  }, [isExpanded, loadRecentChats]);

  // Navigation items for regular users
  const mainNavItems = [
    {
      id: 'new-chat',
      icon: Plus,
      label: 'Novo Chat',
      path: '/chat',
    },
    {
      id: 'conversations',
      icon: MessageSquare,
      label: 'Conversas',
      path: '/conversas',
    },
    {
      id: 'projects',
      icon: FolderClosed, // Special handling for folder
      label: 'Projetos',
      path: '/projetos',
    },
  ];

  // Navigation items for admin - with GRANULAR permission filtering
  // IMPORTANT: can_admin grants access to ALL menu items
  // Each item checks specific permission OR can_admin (which grants everything)
  const allAdminNavItems = [
    {
      id: 'dashboard',
      icon: LayoutDashboard,
      label: 'Dashboard',
      path: '/admin',
      requiresPermission: () => true // Everyone with admin access can see dashboard
    },
    {
      id: 'metrics',
      icon: BarChart3,
      label: 'Métricas',
      path: '/admin/metrics',
      requiresPermission: (u: typeof user) => u?.canViewAnalytics || u?.canAdmin || false
    },
    {
      id: 'workspaces',
      icon: Building2,
      label: 'Workspace',
      path: '/admin/workspaces',
      requiresPermission: (u: typeof user) => u?.canAdmin || u?.canManageWorkspace || false
    },
    {
      id: 'agents',
      icon: Bot,
      label: 'Agentes',
      path: '/admin/agents',
      requiresPermission: (u: typeof user) => u?.canManageAgents || u?.canAdmin || false
    },
    {
      id: 'databases',
      icon: Database,
      label: 'Bases de Conhecimento',
      path: '/admin/databases',
      requiresPermission: (u: typeof user) => u?.canManageAgents || u?.canAdmin || false
    },
    {
      id: 'datasources',
      icon: FileText,
      label: 'Data Sources',
      path: '/admin/datasources',
      requiresPermission: (u: typeof user) => u?.canManageAgents || u?.canAdmin || false
    },
    {
      id: 'settings',
      icon: Cog,
      label: 'Configurações',
      path: '/admin/settings',
      requiresPermission: (u: typeof user) => u?.isWorkspaceAdmin || false // For ALL admin users
    },
  ];

  // Filter admin nav items based on user permissions (NEW SYSTEM)
  const adminNavItems = allAdminNavItems.filter(item => item.requiresPermission(user));

  const handleNavigation = (path: string) => {
    navigate(path);
    // Close sidebar on mobile after navigation
    if (isMobile && isExpanded) {
      onToggle();
    }
  };

  const isActivePath = (path: string) => {
    if (path === '/chat') {
      return location.pathname === '/chat' || location.pathname.startsWith('/chat/');
    }
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    if (path === '/projetos') {
      // Projetos deve ficar ativo tanto em /projetos quanto em /project/:id
      return location.pathname === '/projetos' || location.pathname.startsWith('/project/');
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Render navigation item with animated icon
  const renderNavItem = (item: typeof mainNavItems[0], showLabel: boolean) => {
    const isActive = isActivePath(item.path);
    const isHovered = hoveredItem === item.id;
    const Icon = item.icon;

    return (
      <button
        key={item.id}
        onClick={() => handleNavigation(item.path)}
        onMouseEnter={() => setHoveredItem(item.id)}
        onMouseLeave={() => setHoveredItem(null)}
        className={`
          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg group
          transition-colors duration-200 ease-out
          ${isActive
            ? 'bg-[#F4CC04] text-[#1a1a1a]'
            : 'text-[#999999] hover:bg-[#333333] hover:text-white'
          }
          ${!showLabel ? 'justify-center' : ''}
        `}
        title={!showLabel ? item.label : undefined}
      >
        {/* Special handling for folder icon */}
        {item.id === 'projects' ? (
          <FolderIcon isActive={isActive} isHovered={isHovered} />
        ) : (
          <AnimatedIcon id={item.id} isActive={isActive}>
            <Icon className="w-5 h-5" />
          </AnimatedIcon>
        )}
        {showLabel && (
          <span className="text-sm font-medium whitespace-nowrap">
            {item.label}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobile && isExpanded && (
        <div
          className="fixed inset-0 bg-black/50 z-[49] transition-opacity duration-300"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 h-screen z-50
          bg-[#1a1a1a] border-r border-[#333333]
          flex flex-col
          transition-all duration-300 ease-out
          ${isMobile
            ? isExpanded
              ? 'w-[280px] translate-x-0'
              : 'w-[280px] -translate-x-full'
            : isExpanded
              ? 'w-[250px]'
              : 'w-[60px]'
          }
        `}
      >
        {/* Logo Section */}
        <div className={`h-16 flex items-center border-b border-[#333333] ${showContent ? 'px-4' : 'justify-center'}`}>
          <img
            src={showContent ? '/logotipo-tais.svg' : '/logo-tais.svg'}
            alt="TAIS"
            className={`cursor-pointer object-contain ${showContent ? 'h-8' : 'h-9 w-9'}`}
            onClick={() => handleNavigation('/chat')}
          />
        </div>

        {/* Toggle Button - Hidden on mobile (mobile has header hamburger) */}
        {!isMobile && (
          <div className="py-2 px-2 border-b border-[#333333]">
            <button
              onClick={onToggle}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-lg group
                transition-colors duration-200 ease-out
                ${showContent
                  ? 'bg-[#2a2a2a] text-[#F4CC04]'
                  : 'justify-center text-[#999999] hover:bg-[#333333] hover:text-white'
                }
              `}
              title={showContent ? 'Fechar menu' : 'Abrir menu'}
            >
              <HamburgerIcon isExpanded={isExpanded} />
              {showContent && (
                <span className="text-sm font-medium whitespace-nowrap">
                  Menu
                </span>
              )}
            </button>
          </div>
        )}

        {/* Main Navigation */}
        <nav className="flex-1 py-2 px-2 overflow-y-auto">
          {isAdminRoute ? (
            // Admin Navigation
            <div className="space-y-1">
              {adminNavItems.map(item => renderNavItem(item, showContent))}

              {/* Separator */}
              <div className="my-3 border-t border-[#333333]" />

              {/* Back to Chat */}
              <button
                onClick={() => handleNavigation('/chat')}
                onMouseEnter={() => setHoveredItem('back')}
                onMouseLeave={() => setHoveredItem(null)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg group
                  transition-colors duration-200 ease-out
                  text-[#999999] hover:bg-[#333333] hover:text-white
                  ${!showContent ? 'justify-center' : ''}
                `}
                title={!showContent ? 'Voltar ao Chat' : undefined}
              >
                <ArrowLeft className={`
                  w-5 h-5 flex-shrink-0
                  transition-transform duration-200
                  group-hover:-translate-x-1
                `} />
                {showContent && (
                  <span className="text-sm font-medium whitespace-nowrap">
                    Voltar ao Chat
                  </span>
                )}
              </button>
            </div>
          ) : (
            // Regular Navigation
            <div className="space-y-1">
              {mainNavItems.map(item => renderNavItem(item, showContent))}

              {/* Recent Chats - Only when content is visible */}
              {showContent && (
                <>
                  <div className="my-3 border-t border-[#333333]" />
                  <div className="px-3 py-2">
                    <span className="text-xs text-[#666666] font-medium uppercase tracking-wider">
                      Recentes
                    </span>
                  </div>

                  {isLoadingChats ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-4 h-4 text-[#666666] animate-spin" />
                    </div>
                  ) : recentChats.length > 0 ? (
                    <div className="space-y-0.5">
                      {recentChats.map((chat) => {
                        const isActiveChat = location.pathname === `/chat/${chat.id}`;
                        const chatTitle = chat.chat_title || 'Nova conversa';

                        return (
                          <button
                            key={chat.id}
                            onClick={() => handleNavigation(`/chat/${chat.id}`)}
                            className={`
                              w-full flex items-center gap-2 px-3 py-2 rounded-lg
                              transition-colors duration-200 ease-out text-left
                              ${isActiveChat
                                ? 'bg-[#F4CC04] text-[#1a1a1a]'
                                : 'text-[#999999] hover:bg-[#333333] hover:text-white'
                              }
                            `}
                            title={chatTitle}
                          >
                            <MessageSquare className="w-4 h-4 flex-shrink-0" />
                            <span className="text-sm truncate">
                              {chatTitle}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="px-3 py-2">
                      <span className="text-xs text-[#666666]">
                        Nenhuma conversa recente
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </nav>

        {/* Bottom - User Profile */}
        <div className="py-3 px-2 border-t border-[#333333]">
          {/* User Profile Button - Opens Menu */}
          <button
            onClick={() => setIsUserMenuOpen(true)}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg group
              transition-colors duration-200 ease-out
              text-[#999999] hover:bg-[#333333] hover:text-white
              ${!showContent ? 'justify-center' : ''}
            `}
            title={!showContent ? 'Menu do usuário' : undefined}
          >
            <div className="w-7 h-7 rounded-full bg-[#F4CC04] flex items-center justify-center flex-shrink-0">
              <span className="text-[#1a1a1a] font-bold text-[10px]">{userInitials}</span>
            </div>
            {showContent && (
              <span className="text-sm font-medium whitespace-nowrap">
                {userName || 'Meu Perfil'}
              </span>
            )}
          </button>
        </div>
      </aside>

      {/* User Menu Modal */}
      <UserMenu
        isOpen={isUserMenuOpen}
        onClose={() => setIsUserMenuOpen(false)}
        userName={userName}
        userEmail={userEmail}
        userInitials={userInitials}
        isAdmin={isAdmin}
      />

      {/* Custom animation keyframes */}
      <style>{`
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-10deg); }
          75% { transform: rotate(10deg); }
        }
      `}</style>
    </>
  );
};

export default Sidebar;
